import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface PickupRecord {
  id: string;
  product: string;
  totalWeight: number;
  method: string;
  status: string;
  createTime: string;
  logisticsNo: string | null;
  addressDetail: string | null;
  fee: number;
  assetCodes: string[];
}

const statusMap: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: '审核中', color: 'text-amber-600', bg: 'bg-amber-50' },
  approved: { label: '已审核', color: 'text-green-600', bg: 'bg-green-50' },
  shipped: { label: '已发货', color: 'text-blue-600', bg: 'bg-blue-50' },
  completed: { label: '已完成', color: 'text-green-600', bg: 'bg-green-50' },
  cancelled: { label: '已取消', color: 'text-gray-500', bg: 'bg-gray-50' },
};

export default function PickupRecordsPage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<PickupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) {
        setRecords([]);
        setLoading(false);
        return;
      }

      // 从 pickup_records 表读取提货记录
      const { data } = await supabase
        .from('pickup_records')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      // 同时读取 gold_assets 的已提取资产（作为补充历史）
      const { data: doneAssets } = await supabase
        .from('gold_assets')
        .select('*')
        .eq('user_id', userId)
        .in('status', ['已提取', '已回购'])
        .order('created_at', { ascending: false });

      // 组合 pickup_records 和直接提取的 gold_assets
      const recs: PickupRecord[] = [];
      (data || []).forEach((row) => {
        recs.push({
          id: row.id || `REC-${row.created_at}`,
          product: row.product || '黄金',
          totalWeight: Number(row.total_weight) || 0,
          method: row.method === 'express' ? '快递配送' : '到店自提',
          status: row.status || 'completed',
          createTime: row.create_time || '-',
          logisticsNo: row.logistics_no || null,
          addressDetail: row.address_detail || null,
          fee: Number(row.fee) || 0,
          assetCodes: row.asset_ids || [],
        });
      });

      // 没有在 pickup_records 里记录的已提取资产，也展示出来
      (doneAssets || []).forEach((asset) => {
        const exists = recs.some((r) => r.assetCodes.includes(asset.id));
        if (!exists) {
          recs.push({
            id: `ASSET-${asset.id}`,
            product: asset.product || '黄金',
            totalWeight: Number(asset.weight) || 0,
            method: '-',
            status: 'completed',
            createTime: asset.store_date || '-',
            logisticsNo: null,
            addressDetail: null,
            fee: 0,
            assetCodes: [asset.id],
          });
        }
      });

      setRecords(recs);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-brown-50 pb-6">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <span className="text-gold-400 text-sm font-bold">提货记录</span>
        <div className="w-8" />
      </div>

      {/* Summary */}
      <div className="mx-4 mt-4 bg-gradient-to-br from-brown-900 via-brown-800 to-brown-950 rounded-2xl p-5 text-white relative overflow-hidden">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full border border-gold-400/20"></div>
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gold-500/20 flex items-center justify-center">
            <i className="ri-truck-line text-gold-400 text-lg"></i>
          </div>
          <div>
            <p className="text-white text-lg font-bold">{records.length}</p>
            <p className="text-gold-300/70 text-xs">累计提货记录</p>
          </div>
        </div>
      </div>

      {/* Records List */}
      <div className="mx-4 mt-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <p className="text-brown-500 text-sm mb-3">记录加载失败</p>
            <button
              onClick={fetchData}
              className="px-4 py-2 rounded-full bg-gold-500 text-brown-950 text-xs font-medium cursor-pointer whitespace-nowrap"
            >
              重新加载
            </button>
          </div>
        ) : records.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold-50 flex items-center justify-center">
              <i className="ri-truck-line text-gold-300 text-2xl"></i>
            </div>
            <p className="text-brown-400 text-sm">暂无提货记录</p>
            <button
              onClick={() => navigate('/profile/pickup')}
              className="mt-4 text-gold-600 text-xs cursor-pointer whitespace-nowrap"
            >
              前往申请提货 <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        ) : (
          records.map((rec) => {
            const statusInfo = statusMap[rec.status] || statusMap.completed;
            return (
              <div key={rec.id} className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusInfo.bg} ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    <span className="text-brown-400 text-[10px]">{rec.method}</span>
                  </div>
                  <span className="text-brown-400 text-[10px]">{rec.createTime}</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center flex-shrink-0">
                    <i className="ri-vip-diamond-fill text-brown-950 text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-brown-900 text-sm font-semibold truncate">{rec.product}</h4>
                    <p className="text-brown-400 text-xs mt-0.5">
                      {rec.totalWeight}g · 编码 {rec.assetCodes[0] || '-'}
                      {rec.assetCodes.length > 1 ? ` 等${rec.assetCodes.length}件` : ''}
                    </p>
                  </div>
                  <span className="text-brown-900 text-sm font-bold whitespace-nowrap">
                    {rec.totalWeight}g
                  </span>
                </div>
                {rec.logisticsNo && (
                  <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between">
                    <span className="text-brown-400 text-xs">物流单号</span>
                    <span className="text-brown-900 text-xs font-medium">{rec.logisticsNo}</span>
                  </div>
                )}
                {rec.addressDetail && (
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-brown-400 text-xs">收货地址</span>
                    <span className="text-brown-900 text-xs font-medium text-right truncate max-w-[70%]">
                      {rec.addressDetail}
                    </span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}